import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLenis } from "lenis/react";
import { useNavigate, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { toast } from "sonner";
import { Monogram } from "./Monogram";

const NAV = [
  { label: "About", target: "#about", active: true },
  { label: "Residential", route: "/residential", active: true },
  { label: "Commercial", route: "/commercial", active: true },
  { label: "Builders", route: "/builders", active: true },
  { label: "Developers", route: "/developers", active: true },
  { label: "Investors", route: "/investors", active: true },
  { label: "Locations", target: "#locations", active: true },
  { label: "Podcast", route: "/podcast", active: true },
  { label: "Resources", active: false },
  { label: "Contact", target: "#connect", active: true },
];

export const Navbar = () => {
  const lenis = useLenis();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (item) => {
    setOpen(false);
    if (!item.active) {
      toast("Coming Soon", {
        description: `${item.label} will be available at launch. Join the list below.`,
      });
      return;
    }
    if (item.route) {
      navigate(item.route);
      lenis?.scrollTo(0, { immediate: true });
      return;
    }
    if (item.target === "top") {
      if (pathname === "/") lenis?.scrollTo(0, { duration: 1.4 });
      else navigate("/");
      return;
    }
    if (pathname === "/") lenis?.scrollTo(item.target, { offset: -20, duration: 1.4 });
    else navigate("/" + item.target);
  };

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 1, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,padding] duration-500 ${
          scrolled
            ? "bg-ink/80 backdrop-blur-xl border-b border-charcoal/60 py-4"
            : "bg-transparent border-b border-transparent py-6"
        }`}
        data-testid="navbar"
      >
        <nav className="mx-auto max-w-[1400px] px-6 md:px-12 flex items-center justify-between">
          <button onClick={() => go({ active: true, target: "top" })} data-testid="nav-logo-btn" className="shrink-0">
            <Monogram size={46} />
          </button>

          <div className="hidden 2xl:flex items-center gap-5 xl:gap-7 ml-10">
            {NAV.map((item) => (
              <button
                key={item.label}
                onClick={() => go(item)}
                data-testid={`nav-link-${item.label.toLowerCase()}`}
                className={`group relative text-[0.72rem] uppercase tracking-[0.2em] transition-colors duration-300 ${
                  item.active ? "text-ivory hover:text-gold" : "text-taupe/70 hover:text-taupe"
                }`}
              >
                {item.label}
                <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-gold transition-all duration-500 group-hover:w-full" />
              </button>
            ))}
          </div>

          <button
            className="2xl:hidden text-ivory"
            onClick={() => setOpen(true)}
            data-testid="nav-mobile-open"
            aria-label="Open menu"
          >
            <Menu size={26} strokeWidth={1} />
          </button>
        </nav>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="fixed inset-0 z-[70] bg-ink/97 backdrop-blur-2xl flex flex-col"
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between px-6 py-6 border-b border-charcoal/60">
              <Monogram size={40} />
              <button onClick={() => setOpen(false)} data-testid="nav-mobile-close" aria-label="Close menu">
                <X size={26} strokeWidth={1} className="text-ivory" />
              </button>
            </div>
            <div className="flex-1 flex flex-col justify-center px-8 gap-1">
              {NAV.map((item, i) => (
                <motion.button
                  key={item.label}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 + i * 0.05 }}
                  onClick={() => go(item)}
                  data-testid={`nav-mobile-link-${item.label.toLowerCase()}`}
                  className="text-left py-3 font-serif text-4xl text-ivory flex items-center justify-between border-b border-charcoal/40"
                >
                  <span>{item.label}</span>
                  {!item.active && (
                    <span className="overline text-[0.55rem] text-taupe/60">Soon</span>
                  )}
                </motion.button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
